'use client';

import { 
  ControlType, 
  CalculatorId, 
  CalculatorType, 
  ControlIds, 
  type CalculatorConfig, 
  type ControlValues,
  type CalculatorControl,
  type SingleColumnSelectControl,
  type TextControl,
  type SelectControl,
  type NumberControl
} from '../../../lib/calculators/calculator-config';
import { TableDataJSON } from '../../../src/types/table-json';
import { LazyTabLabECharts } from '@/components/ui/charts/lazy-tab-lab-echarts';
import type { EChartsOption, PieSeriesOption } from 'echarts';
import { nightingaleRoseDescription } from './nightingale-rose.description';
import { pieChartConfig, pieChartControls, type PieChartControlValues, getPieChartConfig } from '../pie-chart/pie-chart.config';

// Extend pie chart control values with Nightingale-specific ones
export interface NightingaleRoseControlValues extends Omit<PieChartControlValues, 'roseType' | 'sortValues'> {
  [ControlIds.SortValues]: 'true' | 'false';
  [ControlIds.RoseType]: 'radius' | 'area';
}

// Add Nightingale-specific controls
export const nightingaleRoseControls: { tabs: { title: string; controls: readonly CalculatorControl[]; }[] } = {
  tabs: [
    {
      title: 'Data',
      controls: pieChartControls.tabs[0]?.controls ?? []
    },
    {
      title: 'Appearance',
      controls: [
        ...(pieChartControls.tabs[1]?.controls ?? []),
        {
          id: ControlIds.SortValues,
          type: ControlType.Select,
          label: 'Sort segments',
          options: [
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' }
          ],
          defaultValue: 'true'
        } satisfies SelectControl,
        {
          id: ControlIds.RoseType,
          type: ControlType.Select,
          label: 'Rose type',
          options: [
            { label: 'Radius', value: 'radius' },
            { label: 'Area', value: 'area' }
          ],
          defaultValue: 'radius'
        } satisfies SelectControl
      ]
    }
  ]
};

// Extend pie chart config for Nightingale Rose chart
function getNightingaleRoseConfig(pieConfig: EChartsOption, sortValues: boolean, roseType: 'radius' | 'area'): EChartsOption {
  const roseConfig = { 
    ...pieConfig,
    toolbox: {
      show: true,
      feature: {
        saveAsImage: {
          title: 'Save as Image',
          name: 'nightingale-rose-chart'
        },
        dataZoom: {
          title: {
            zoom: 'Zoom',
            back: 'Zoom Reset'
          }
        }
      }
    }
  };
  
  // Modify the series configuration for Nightingale Rose chart
  if (roseConfig.series && Array.isArray(roseConfig.series)) {
    roseConfig.series = roseConfig.series.map(series => {
      if (series.type !== 'pie') return series;

      const pieSeries = series as PieSeriesOption;
      const data = [...(pieSeries.data || [])];
      if (sortValues && Array.isArray(data)) {
        data.sort((a, b) => {
          const aValue = typeof a === 'object' && 'value' in a ? Number(a.value) : 0;
          const bValue = typeof b === 'object' && 'value' in b ? Number(b.value) : 0;
          return bValue - aValue;
        });
      }

      const outerRadius = typeof pieSeries.radius === 'string' ? pieSeries.radius : '70%';

      return {
        ...pieSeries,
        type: 'pie' as const,
        roseType,
        data,
        radius: [0, outerRadius],
        itemStyle: {
          ...pieSeries.itemStyle,
          borderRadius: 0 // Remove border radius for cleaner look
        },
        label: pieSeries.label // Keep original label configuration from pie chart
      };
    });
  }

  return roseConfig;
}

export const nightingaleRoseConfig: CalculatorConfig<CalculatorId.NightingaleChart> = {
  ...pieChartConfig,
  id: CalculatorId.NightingaleChart,
  name: 'Nightingale Rose Chart',
  initialData: TableDataJSON.from({
    columns: [
      { id: 'month', name: 'Month', values: ['Jan', 'Feb', 'Mar', 'Apr', 'May'] },
      { id: 'cases', name: 'Cases', values: ['120', '150', '200', '180', '130'] }
    ]
  }),
  initialControlValues: {
    category: 'month',
    value: 'cases',
    title: 'Monthly Case Distribution',
    showLabels: 'true',
    labelPosition: 'outside',
    showPercentages: 'true',
    radius: 75,
    sortValues: 'true',
    roseType: 'radius'
  },
  controls: nightingaleRoseControls,
  getVisualizationData: ({ data, controls }) => {
    // Get base pie chart configuration
    const pieConfig = pieChartConfig.getVisualizationData({ data, controls }) as EChartsOption;
    if (!pieConfig) return null;

    // Type assert the controls to our specific type
    const roseControls = controls as NightingaleRoseControlValues;
    
    // Convert to Nightingale Rose chart configuration
    return getNightingaleRoseConfig(
      pieConfig,
      roseControls[ControlIds.SortValues] === 'true',
      roseControls[ControlIds.RoseType]
    );
  }
}; 